import type { PageSnapshot } from '../types/assistant'

/** Refuse oversized selections instead of silently acting on a subset. */
export const MAX_PAGE_SELECTION = 1000
export const PAGE_SNAPSHOT_TYPES = [
  'task',
  'ticket',
  'project',
  'deal',
  'entity',
  'contact',
  'contact',
  'note',
  'document',
  'event',
  'contract',
  'invoice',
  'supplier',
  'collection',
  'time',
  'help',
] as const

export function pageSnapshotModule(value: unknown): string | undefined {
  const type = typeof value === 'string' ? value.toLowerCase() : ''
  return PAGE_SNAPSHOT_TYPES.includes(
    type as (typeof PAGE_SNAPSHOT_TYPES)[number],
  )
    ? type
    : undefined
}

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid page context')
  }
  return value as Record<string, unknown>
}

function string(value: unknown, max: number): string {
  if (typeof value !== 'string' || value.length > max) {
    throw new Error('Page context exceeds its supported limits')
  }
  return [...value]
    .filter(character => {
      const code = character.charCodeAt(0)
      return code >= 32 || code === 9 || code === 10 || code === 13
    })
    .join('')
}

function recordType(value: unknown): string {
  const type = pageSnapshotModule(value)
  if (!type) throw new Error('Unsupported page context record type')
  return type
}

function id(value: unknown): string {
  const result = string(value, 128)
  if (!/^[a-zA-Z0-9][a-zA-Z0-9_.:-]*$/.test(result)) {
    throw new Error('Invalid page context record identifier')
  }
  return result
}

/** Validate at the API boundary and clone all data; never keep publisher refs. */
export function parsePageSnapshot(value: unknown): PageSnapshot | undefined {
  if (value === undefined || value === null) return undefined
  const input = object(value)
  if (input.version !== 1) throw new Error('Unsupported page context version')
  const route = object(input.route)
  const pathname = string(route.pathname, 1024)
  if (!pathname.startsWith('/') || pathname.startsWith('//')) {
    throw new Error('Invalid page context route')
  }
  const capturedAt = string(input.capturedAt, 40)
  if (!Number.isFinite(Date.parse(capturedAt))) {
    throw new Error('Invalid page context capture time')
  }
  const result: PageSnapshot = {
    version: 1,
    route: { pathname, title: string(route.title, 240) },
    capturedAt,
  }
  if (input.module !== undefined) result.module = recordType(input.module)
  if (input.record !== undefined) {
    const record = object(input.record)
    result.record = {
      type: recordType(record.type),
      id: id(record.id),
      name: string(record.name, 240),
    }
  }
  if (input.selection !== undefined) {
    const selection = object(input.selection)
    if (
      !Array.isArray(selection.ids) ||
      selection.ids.length > MAX_PAGE_SELECTION
    ) {
      throw new Error(
        `Select at most ${MAX_PAGE_SELECTION} records to ask Benny`,
      )
    }
    const ids = [...new Set(selection.ids.map(id))]
    const labels =
      selection.labels === undefined ? undefined : object(selection.labels)
    result.selection = {
      type: recordType(selection.type),
      ids,
      ...(labels
        ? {
            labels: Object.fromEntries(
              ids
                .filter(key => labels[key] !== undefined)
                .map(key => [key, string(labels[key], 160)]),
            ),
          }
        : {}),
    }
  }
  if (input.filters !== undefined) {
    const filters = object(input.filters)
    result.filters = {
      summary: string(filters.summary, 1024),
      ...(filters.scql !== undefined
        ? { scql: string(filters.scql, 4096) }
        : {}),
    }
  }
  if (input.view !== undefined) {
    const view = object(input.view)
    const columns = view.columns
    if (
      columns !== undefined &&
      (!Array.isArray(columns) || columns.length > 100)
    ) {
      throw new Error('Too many page context columns')
    }
    result.view = {
      ...(Array.isArray(columns)
        ? { columns: columns.map(column => string(column, 80)) }
        : {}),
      ...(view.groupBy !== undefined
        ? { groupBy: string(view.groupBy, 80) }
        : {}),
      ...(view.sort !== undefined ? { sort: string(view.sort, 160) } : {}),
    }
  }
  return result
}

export function pageSnapshotLabel(snapshot: PageSnapshot): string {
  if (snapshot.selection?.ids.length) {
    const { type, ids } = snapshot.selection
    const noun = type === 'entity' ? 'record' : type
    return `Using ${ids.length} selected ${noun}${ids.length === 1 ? '' : 's'}`
  }
  if (snapshot.record) return `Using ${snapshot.record.name}`
  return snapshot.filters?.summary
    ? `Using filtered ${snapshot.module ?? 'page'} view`
    : `Using ${snapshot.route.title || 'this page'}`
}
